import {ImageUploader} from '@/containers/ClubPlus/CreateTier/phases/Display/ImageUploader';
import {Divider} from '@/components/atoms';
import {useFormContext} from 'react-hook-form';
import {Font, Icon} from '@pepsico-ds/ui';
import {useIntl} from 'react-intl';
import {useEffect, useMemo} from 'react';
import FormInputController from '@/components/molecules/FormInputController';
import PropTypes from 'prop-types';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {getLegacyLanguageCode} from '@/utils/languageFallback';

export const DisplayForm = ({language}) => {
  const {control, getValues, setValue, watch} = useFormContext();
  const {formatMessage} = useIntl();
  const countryCode = useAppSelector(selectCountryCode);

  const otherLanguages = useMemo(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || [];
    return languages
      .map((lang) => lang.code)
      .filter((code) => code !== language);
  }, [countryCode, language]);

  useEffect(() => {
    const content = getValues('content');
    if (!content[language]) {
      const legacyCode = getLegacyLanguageCode(language);
      if (legacyCode && content[legacyCode]) {
        setValue(`content.${language}`, content[legacyCode]);
      }
    }
  }, [language, getValues, setValue]);

  const [imageTierIcon, bannerImage, detailsImage] = watch([
    `content.${language}.imageTierIcon`,
    `content.${language}.bannerImage`,
    `content.${language}.detailsImage`
  ]);

  useEffect(() => {
    if (!imageTierIcon) {
      setValue(`content.${language}.imageTierIconObject`, null, {
        shouldDirty: false
      });
    }
    otherLanguages.forEach((otherLang) => {
      const currentValue = getValues(`content.${otherLang}.imageTierIcon`);
      if (currentValue !== imageTierIcon) {
        const valueToSet = imageTierIcon
          ? Number(imageTierIcon)
          : imageTierIcon;
        setValue(`content.${otherLang}.imageTierIcon`, valueToSet, {
          shouldDirty: true
        });
        if (!imageTierIcon) {
          setValue(`content.${otherLang}.imageTierIconObject`, null, {
            shouldDirty: false
          });
        }
      }
    });
  }, [imageTierIcon, language, otherLanguages, setValue, getValues]);

  useEffect(() => {
    if (!bannerImage) {
      setValue(`content.${language}.bannerImageObject`, null, {
        shouldDirty: false
      });
    }
    otherLanguages.forEach((otherLang) => {
      const currentValue = getValues(`content.${otherLang}.bannerImage`);
      if (currentValue !== bannerImage) {
        const valueToSet = bannerImage ? Number(bannerImage) : bannerImage;
        setValue(`content.${otherLang}.bannerImage`, valueToSet, {
          shouldDirty: true
        });
        if (!bannerImage) {
          setValue(`content.${otherLang}.bannerImageObject`, null, {
            shouldDirty: false
          });
        }
      }
    });
  }, [bannerImage, language, otherLanguages, setValue, getValues]);

  useEffect(() => {
    if (!detailsImage) {
      setValue(`content.${language}.detailsImageObject`, null, {
        shouldDirty: false
      });
    }
    otherLanguages.forEach((otherLang) => {
      const currentValue = getValues(`content.${otherLang}.detailsImage`);
      if (currentValue !== detailsImage) {
        const valueToSet = detailsImage ? Number(detailsImage) : detailsImage;
        setValue(`content.${otherLang}.detailsImage`, valueToSet, {
          shouldDirty: true
        });
        if (!detailsImage) {
          setValue(`content.${otherLang}.detailsImageObject`, null, {
            shouldDirty: false
          });
        }
      }
    });
  }, [detailsImage, language, otherLanguages, setValue, getValues]);

  const handleFieldChange = (field) => (value) => {
    field.onChange(value);
  };

  const getImageData = (imageType) => {
    const content = getValues()?.content?.[language];
    if (!content) return null;

    const imageMap = {
      imageTierIcon: content.imageTierIconObject?.data,
      bannerImage: content.bannerImageObject?.data,
      detailsImage: content.detailsImageObject?.data
    };

    return imageMap[imageType] || null;
  };

  const getImageUploadLabel = (label) => {
    return (
      <span className='flex gap-2'>
        {formatMessage({id: label})}
        <Icon icon='info' size='small' />
      </span>
    );
  };

  useEffect(() => {
    const values = getValues();
    setValue(`content.${language}.tierName`, values.name);
  }, [getValues, language, setValue]);

  return (
    <>
      <div className='my-5'>
        <Font variant='bold' size='body' textColorClass='text-white'>
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_HOMEPAGE_TITLE'})}
        </Font>
      </div>
      <div className='flex w-full justify-start gap-6'>
        <ImageUploader
          control={control}
          name={`content.${language}.imageTierIcon`}
          label={getImageUploadLabel(
            'CLUB_PLUS.TIER_CREATE_FORM_TIER_NAME_LABEL'
          )}
          initialImageData={getImageData('imageTierIcon')}
        />
        <div>
          <Font variant='regular' size='xsmall' textColorClass='text-white'>
            {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_TIER_LABEL'})}
          </Font>
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.tierName`}
            inputType='text'
            labelText='CLUB_PLUS.TIER_CREATE_TIER_LABEL'
            placeholderText='CLUB_PLUS.TIER_CREATE_NAME_PLACEHOLDER'
            fieldProps={{disabled: true, visible: true}}
            hideLabel
          />
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.displayDescription`}
            inputType='text'
            labelText='CLUB_PLUS.TIER_CREATE_FORM_DISPLAY_DESCRIPTION_INPUT_LABEL'
            helperText='CLUB_PLUS.TIER_CREATE_FORM_HELPER_TEXT'
            placeholderText='CLUB_PLUS.TIER_CREATE_FORM_DISPLAY_DESCRIPTION_PLACEHOLDER'
            fieldProps={{visible: true}}
            hideLabel={false}
            maxLength={30}
          />
        </div>
      </div>
      <Divider />
      <div className='my-5'>
        <Font variant='bold' size='body' textColorClass='text-white'>
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DETAIL_BANNER'})}
        </Font>
      </div>
      <div className='tier-display-form flex w-full justify-start gap-6'>
        <ImageUploader
          control={control}
          name={`content.${language}.bannerImage`}
          label={getImageUploadLabel(
            'CLUB_PLUS.TIER_CREATE_FORM_HOMEPAGE_IMAGE'
          )}
          initialImageData={getImageData('bannerImage')}
        />
        <div className='flex flex-col gap-3'>
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.bannerHeader`}
            inputType='textarea'
            labelText='CLUB_PLUS.TIER_CREATE_FORM_BANNER_HEADER_LABEL'
            helperText='CLUB_PLUS.TIER_CREATE_TEXTAREA_HELPER_TEXT'
            placeholderText='CLUB_PLUS.TIER_CREATE_FORM_BANNER_HEADER_PLACEHOLDER'
          />
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.bannerDescription`}
            inputType='textarea'
            labelText='CLUB_PLUS.TIER_CREATE_REVIEW_DESCRIPTION_LABEL'
            helperText='CLUB_PLUS.TIER_CREATE_TEXTAREA_HELPER_TEXT'
            placeholderText='CLUB_PLUS.TIER_CREATE_REVIEW_DESCRIPTION_LABEL'
          />
        </div>
      </div>
      <Divider />
      <div className='my-5'>
        <Font variant='bold' size='body' textColorClass='text-white'>
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DETAIL_PAGE'})}
        </Font>
      </div>
      <div className='tier-display-form flex w-full justify-start gap-6'>
        <ImageUploader
          control={control}
          name={`content.${language}.detailsImage`}
          label={getImageUploadLabel(
            'CLUB_PLUS.TIER_CREATE_FORM_DETAIL_PAGE_IMG'
          )}
          initialImageData={getImageData('detailsImage')}
        />
        <div className='flex flex-col gap-3'>
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.detailsDescription`}
            inputType='textarea'
            labelText='CLUB_PLUS.TIER_CREATE_FORM_DESCRIPTION_LABEL'
            helperText='CLUB_PLUS.TIER_CREATE_TEXTAREA_HELPER_TEXT'
            placeholderText='CLUB_PLUS.TIER_CREATE_FORM_DESCRIPTION_PLACEHOLDER'
          />
          <FormInputController
            onChange={handleFieldChange}
            name={`content.${language}.detailsTermsAndConditions`}
            inputType='textarea'
            labelText='CLUB_PLUS.TIER_CREATE_FORM_TERMS_CONDITIONS_LABEL'
            helperText='CLUB_PLUS.TIER_CREATE_TEXTAREA_HELPER_TEXT'
            placeholderText='CLUB_PLUS.TIER_CREATE_FORM_DESCRIPTION_PLACEHOLDER'
          />
        </div>
      </div>
      <Divider />
      <div className='pb-28'></div>
    </>
  );
};

DisplayForm.propTypes = {
  language: PropTypes.string.isRequired,
  // Passed from Display container for API parity; form state is driven by react-hook-form.
  tierData: PropTypes.shape({
    content: PropTypes.object
  })
};
